import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { api } from "@/lib/api";
import { ArrowRight, Check, Eye, Sparkles, Star, X } from "lucide-react";

/**
 * Step 1 of the quotation flow.
 * - Loads the template catalog from /api/quotation-templates
 * - Pre-selects the agent's `default_template_id` if set
 * - On "Select": navigates to /quotations/new?template=<id>
 * - On "Preview": opens a modal showing the sample cover
 *
 * Existing quotations & PDFs are untouched — classic is the default.
 */
export default function QuotationTemplateChooser() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const editingQid = params.get("editing"); // optional - reserved for future "switch template on existing"
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState("classic");
  const [preview, setPreview] = useState(null); // template object or null
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [tplRes, profRes] = await Promise.all([
          api.get("/quotation-templates"),
          api.get("/profile").catch(() => ({ data: {} })),
        ]);
        setTemplates(tplRes.data || []);
        const def = profRes?.data?.default_template_id;
        if (def) setSelected(def);
      } catch {
        toast.error("Could not load templates");
      } finally { setLoading(false); }
    })();
  }, []);

  const proceed = () => {
    if (!selected) return toast.error("Pick a template first");
    nav(`/quotations/new?template=${encodeURIComponent(selected)}`);
  };

  const grid = useMemo(() => templates, [templates]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar role="agent" />
      <main className="flex-1 p-8 lg:p-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
            <div>
              <div className="overline text-emerald-700">New Quotation · Step 1 of 11</div>
              <h1 className="font-editorial text-3xl text-slate-900 mt-1">Choose a template</h1>
              <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                Every template shows the same trip in a different visual style. Your agent branding stays consistent across all of them. You can preview one before committing.
              </p>
            </div>
            <button
              data-testid="tpl-continue-btn"
              onClick={proceed}
              className="inline-flex items-center gap-2 bg-[#16A34A] hover:bg-emerald-700 text-white px-5 py-2.5 rounded text-sm font-semibold"
            >
              Continue with {templates.find(t => t.id === selected)?.name || "Classic"}
              <ArrowRight size={15} />
            </button>
          </div>

          {loading ? (
            <div className="text-sm text-slate-500">Loading templates…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {grid.map((t) => {
                const isSel = selected === t.id;
                const isNew = (t.badges || []).includes("New");
                const isRec = (t.badges || []).includes("Recommended");
                return (
                  <div
                    key={t.id}
                    data-testid={`tpl-card-${t.id}`}
                    className={`group bg-white border rounded-lg p-5 transition-all ${isSel ? "border-emerald-500 ring-2 ring-emerald-200" : "border-border hover:border-emerald-400"}`}
                  >
                    {/* Preview strip (color-block placeholder) */}
                    <div className="rounded overflow-hidden h-40 mb-4 relative"
                         style={{ background: `linear-gradient(135deg, ${t.palette?.primary || "#0F1E5B"} 0%, ${t.palette?.accent || "#16A34A"} 100%)` }}>
                      <div className="absolute inset-0 p-4 flex flex-col justify-between text-white">
                        <div className="flex justify-between items-start">
                          <div className="text-[10px] tracking-widest font-semibold opacity-80">{(t.tagline || "").toUpperCase()}</div>
                          <div className="flex gap-1">
                            {isRec && <span className="text-[9px] uppercase tracking-widest bg-white/20 rounded-full px-2 py-0.5 font-semibold">Recommended</span>}
                            {isNew && <span className="text-[9px] uppercase tracking-widest bg-white/90 text-slate-900 rounded-full px-2 py-0.5 font-semibold">New</span>}
                          </div>
                        </div>
                        <div>
                          <div className="font-editorial text-2xl leading-none">{t.name}</div>
                          <div className="text-[10px] uppercase tracking-widest opacity-80 mt-1.5">TRAVELOBLE</div>
                        </div>
                      </div>
                      {isSel && (
                        <div className="absolute top-2 left-2 bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
                          <Check size={13} strokeWidth={3} />
                        </div>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500 mt-1 line-clamp-2 min-h-[32px]">{t.description}</div>
                    <div className="text-[10.5px] uppercase tracking-widest text-slate-400 mt-2 min-h-[14px]">{t.suitable_for || ""}</div>
                    <div className="flex items-center gap-2 mt-4">
                      <button
                        data-testid={`tpl-preview-${t.id}`}
                        onClick={() => setPreview(t)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 border border-border rounded px-3 py-2 text-xs hover:border-emerald-500"
                      >
                        <Eye size={12} /> Preview
                      </button>
                      <button
                        data-testid={`tpl-select-${t.id}`}
                        onClick={() => setSelected(t.id)}
                        className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded px-3 py-2 text-xs font-semibold ${isSel ? "bg-emerald-500 text-white" : "bg-[#0F1E5B] text-white hover:bg-emerald-700"}`}
                      >
                        {isSel ? "Selected" : "Select"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <div className="overline text-emerald-700">Template preview</div>
                <div className="font-editorial text-2xl text-slate-900">{preview.name}</div>
                <div className="text-xs text-slate-500 mt-1">{preview.tagline}</div>
              </div>
              <button data-testid="tpl-close-preview" onClick={() => setPreview(null)} className="p-1.5"><X size={18} /></button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="rounded-lg h-96 relative overflow-hidden"
                   style={{ background: `linear-gradient(135deg, ${preview.palette?.primary} 0%, ${preview.palette?.accent} 100%)` }}>
                <div className="absolute inset-0 p-8 text-white flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <div className="text-xs tracking-widest opacity-80">{(preview.tagline || "").toUpperCase()}</div>
                    <div className="text-xs tracking-widest opacity-70">TRAVELOBLE</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-widest opacity-70 mb-1">SAMPLE TRIP</div>
                    <div className="font-editorial text-4xl leading-tight">Bali Getaway<br />6 Nights · 7 Days</div>
                    <div className="text-xs opacity-70 mt-4">A cinematic preview — your actual quotation will use this palette across every page.</div>
                  </div>
                </div>
              </div>
              <div className="mt-5 text-sm text-slate-600 leading-relaxed">{preview.description}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(preview.badges || []).map((b) => (
                  <span key={b} className="text-[10px] uppercase tracking-widest bg-slate-100 rounded-full px-2 py-1 font-semibold text-slate-700">{b}</span>
                ))}
                {preview.suitable_for && (
                  <span className="text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-700 rounded-full px-2 py-1 font-semibold">{preview.suitable_for}</span>
                )}
              </div>
            </div>
            <div className="p-5 border-t border-border flex justify-end gap-2">
              <button onClick={() => setPreview(null)} className="text-xs px-3 py-2 border border-border rounded">Close</button>
              <button
                data-testid="tpl-select-from-preview"
                onClick={() => { setSelected(preview.id); setPreview(null); }}
                className="text-xs px-4 py-2 rounded bg-[#16A34A] text-white font-semibold inline-flex items-center gap-1.5"
              >
                <Star size={12} /> Use this template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
